import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { STUDENT_NAV } from "@/pages/student/StudentHome";
import { LevelBadge } from "@/components/LevelBadge";
import { Card } from "@/components/ui/card";
import { DOMAINS, SKILLS } from "@/lib/framework";
import { BookOpen, Clock, GraduationCap, Sparkles } from "lucide-react";

// Example Learning Academy micro-courses mapped to each transferable skill.
const COURSES = {
  proj_task_mgmt: [
    { code: "LA-SM101", name: "Self-Management & Personal Productivity", length: "3 modules · 2 hrs",
      desc: "Plan, prioritize, and follow through on commitments reliably." },
    { code: "LA-SM210", name: "Time & Task Systems", length: "3 modules · 2 hrs",
      desc: "Build a personal system to manage workload and meet deadlines." },
  ],
  prof_ethics: [
    { code: "LA-ER101", name: "Professional Ethics in Practice", length: "3 modules · 2 hrs",
      desc: "Navigate real workplace dilemmas with integrity and accountability." },
    { code: "LA-ER150", name: "Accountability & Professional Conduct", length: "2 modules · 1.5 hrs",
      desc: "Own your commitments and act with consistent professionalism." },
  ],
  digital_adapt: [
    { code: "LA-LD101", name: "Leading Without Authority", length: "3 modules · 2.5 hrs",
      desc: "Influence, delegate, and guide peers toward a shared goal." },
    { code: "LA-LD220", name: "Project Leadership & Ownership", length: "3 modules · 2 hrs",
      desc: "Take initiative and guide team efforts through to completion." },
  ],
  critical_solving: [
    { code: "LA-CT130", name: "Critical Thinking Foundations", length: "4 modules · 3 hrs",
      desc: "Evaluate evidence and reason to sound, defensible conclusions." },
    { code: "LA-CT240", name: "Analytical Reasoning Lab", length: "3 modules · 2.5 hrs",
      desc: "Practice structured analysis on real-world scenarios." },
  ],
  data_literacy: [
    { code: "LA-PS120", name: "Structured Problem Solving", length: "4 modules · 3 hrs",
      desc: "Frame problems, generate options, and decide effectively." },
    { code: "LA-PS230", name: "Decision-Making Under Uncertainty", length: "3 modules · 2.5 hrs",
      desc: "Make sound decisions when information is incomplete." },
  ],
  systems_thinking: [
    { code: "LA-MC140", name: "Metacognition & Reflective Practice", length: "3 modules · 2.5 hrs",
      desc: "Reflect on how you learn to improve your strategies." },
    { code: "LA-MC250", name: "Strategic & Analytical Thinking", length: "2 modules · 2 hrs",
      desc: "Plan and self-monitor toward stronger performance." },
  ],
  creative_synthesis: [
    { code: "LA-GM160", name: "Growth Mindset in Action", length: "3 modules · 2 hrs",
      desc: "Embrace challenges and learn from feedback and setbacks." },
    { code: "LA-GM270", name: "Innovation & Continuous Improvement", length: "4 modules · 3 hrs",
      desc: "Seek new ideas and pursue ongoing improvement." },
  ],
  collab_leadership: [
    { code: "LA-CO170", name: "Teamwork & Group Collaboration", length: "3 modules · 2.5 hrs",
      desc: "Work effectively in teams and navigate group dynamics." },
    { code: "LA-CO280", name: "Facilitation & Conflict Resolution", length: "3 modules · 2 hrs",
      desc: "Run effective meetings and resolve conflict constructively." },
  ],
  intercultural_comm: [
    { code: "LA-CM180", name: "Written & Verbal Communication", length: "3 modules · 2 hrs",
      desc: "Communicate clearly and persuasively to any audience." },
    { code: "LA-CM290", name: "Professional Presence & Storytelling", length: "4 modules · 3 hrs",
      desc: "Present ideas with confidence in writing and in person." },
  ],
  self_resilience: [
    { code: "LA-RS190", name: "Resilience & Stress Management", length: "3 modules · 2.5 hrs",
      desc: "Recover from setbacks and stay effective under pressure." },
    { code: "LA-RS295", name: "Adaptability & Persistence", length: "3 modules · 2 hrs",
      desc: "Adapt to change and persist through new challenges." },
  ],
};

export default function LearningAcademy() {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    api.get("/student/me").then(({ data }) => setProfile(data.profile)).catch(() => {});
  }, []);

  const scoreFor = (id) => profile?.skills.find((s) => s.id === id);

  return (
    <DashboardShell
      title="Learning Academy"
      subtitle="Example micro-courses to strengthen each transferable skill"
      nav={STUDENT_NAV}
    >
      <Card className="mb-6 border-[#78be21]/40 bg-[#78be21]/5 p-4">
        <p className="flex items-center gap-2 text-sm text-slate-700">
          <Sparkles className="h-5 w-5 text-[#5fa114]" />
          These are example placeholders. In the full rollout, each module links to live courseware and reports completion back into your TSB.
        </p>
      </Card>

      <div className="space-y-8">
        {DOMAINS.map((d) => (
          <div key={d.id} data-testid={`academy-domain-${d.id}`}>
            <div className="mb-3 flex items-center gap-2">
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: d.color }} />
              <h2 className="font-heading text-xl font-bold text-[#003057]">{d.name}</h2>
            </div>
            <div className="space-y-5">
              {SKILLS.filter((s) => s.domain === d.id).map((s) => {
                const sc = scoreFor(s.id);
                return (
                  <div key={s.id} data-testid={`academy-skill-${s.id}`}>
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <h3 className="font-heading text-base font-bold text-slate-800">{s.name}</h3>
                      {sc && (
                        <>
                          <span className="font-mono-num text-sm font-bold text-slate-500">{sc.score.toFixed(2)}</span>
                          <LevelBadge level={sc.level} />
                        </>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {COURSES[s.id].map((c) => (
                        <div key={c.code} className="rounded-xl border border-slate-200 bg-white p-4 card-elevate">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${d.color}18` }}>
                                <BookOpen className="h-4.5 w-4.5" style={{ width: 18, height: 18, color: d.color }} />
                              </div>
                              <div>
                                <p className="font-heading text-sm font-bold text-[#003057]">{c.name}</p>
                                <p className="font-mono-num text-[11px] font-semibold uppercase tracking-wide text-slate-400">{c.code}</p>
                              </div>
                            </div>
                          </div>
                          <p className="mt-3 text-sm leading-relaxed text-slate-600">{c.desc}</p>
                          <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-slate-400">
                            <Clock className="h-3.5 w-3.5" /> {c.length}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}
